import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, Star, Trash2, X } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { api, errMessage } from "../lib/api";

const CATEGORIES = [
  ["engaging", "Engaging"],
  ["age_appropriate", "Age-appropriate"],
  ["easy_to_use", "Easy to use"],
  ["educational_value", "Educational value"],
  ["accessibility", "Accessibility"],
];

const Stars = ({ value, onChange, testPrefix }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={onChange ? () => onChange(n) : undefined}
        aria-label={`${n} star${n > 1 ? "s" : ""}`}
        data-testid={`${testPrefix}-star-${n}`}
        className={onChange ? "cursor-pointer" : "cursor-default"}>
        <Star className={`h-5 w-5 ${n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} aria-hidden="true" />
      </button>
    ))}
  </div>
);

const RateDialog = ({ activity, onSaved, triggerLabel }) => {
  const [open, setOpen] = useState(false);
  const mine = activity.my_rating;
  const [stars, setStars] = useState(mine?.stars || 0);
  const [cats, setCats] = useState(mine?.categories || {});
  const [comment, setComment] = useState(mine?.private_comment || "");
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!stars) return toast.error("Pick a star rating first");
    setSaving(true);
    try {
      await api.put(`/ratings/activities/${activity.submission_id}`, {
        stars, categories: cats, private_comment: comment,
      });
      if (photoFile) {
        const form = new FormData();
        form.append("file", photoFile);
        try {
          await api.post(`/ratings/activities/${activity.submission_id}/photo`, form);
          toast.success("Rating and photo saved");
        } catch (photoErr) {
          toast.error(`Rating saved, but the photo failed: ${errMessage(photoErr)}`);
        }
        setPhotoFile(null);
      } else {
        toast.success(mine ? "Rating updated" : "Thanks for rating!");
      }
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const removePhoto = async () => {
    try {
      await api.delete(`/ratings/activities/${activity.submission_id}/photo`);
      toast.success("Photo removed");
      onSaved();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const setGalleryConsent = async (share) => {
    try {
      await api.put(`/ratings/activities/${activity.submission_id}/photo/consent`, { share });
      toast.success(share
        ? "Thanks! Your photo goes to our review team before it appears in the gallery"
        : "Removed from the gallery");
      onSaved();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={mine ? "outline" : "default"} size="sm" className="rounded-full h-8"
          data-testid={`rate-button-${activity.submission_id}`}>
          {mine ? "Edit rating" : (triggerLabel || "Rate")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="rate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{activity.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Stars value={stars} onChange={setStars} testPrefix="rate-input" />
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">What worked well? (optional)</p>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={Boolean(cats[key])}
                    onCheckedChange={(v) => setCats({ ...cats, [key]: Boolean(v) })}
                    data-testid={`rate-category-${key}`} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500}
              placeholder="Private feedback for the review team (optional)"
              data-testid="rate-comment-input" />
            <p className="text-xs text-muted-foreground" data-testid="rate-privacy-warning">
              Private — never shown publicly. Please don't include personal, medical, school or
              identifying information about any child or adult.
            </p>
          </div>
          <div className="space-y-1.5" data-testid="rate-photo-block">
            <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <Camera className="h-3.5 w-3.5" aria-hidden="true" />
              Photo of your child's creation (optional)
            </p>
            {mine?.photo_url && !photoFile && (
              <div className="flex items-center gap-2" data-testid="rate-photo-current">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${mine.photo_url}`}
                  alt="Your creation photo" className="h-16 rounded-lg border border-border object-cover" />
                <Button variant="ghost" size="sm" className="rounded-full h-7 text-destructive"
                  onClick={removePhoto} data-testid="rate-photo-remove">
                  <X className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Remove photo
                </Button>
              </div>
            )}
            {mine?.photo_url && (
              <div className="space-y-1" data-testid="rate-gallery-block">
                <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                  <Checkbox checked={Boolean(mine.photo_gallery_consent)}
                    onCheckedChange={(v) => setGalleryConsent(Boolean(v))}
                    data-testid="rate-gallery-consent" />
                  Share in the community creations gallery
                  {mine.photo_gallery_status && (
                    <Badge variant={mine.photo_gallery_status === "approved" ? "secondary"
                      : mine.photo_gallery_status === "rejected" ? "destructive" : "outline"}
                      className="text-[10px]" data-testid="rate-gallery-status">
                      {mine.photo_gallery_status}
                    </Badge>
                  )}
                </label>
                <p className="text-xs text-muted-foreground">
                  Our review team approves every photo first. Shown without any names — untick any time to remove it.
                </p>
              </div>
            )}
            <input type="file" accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
              className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-full file:border-0 file:bg-accent file:px-3 file:py-1.5 file:text-xs file:font-semibold"
              data-testid="rate-photo-input" />
            <p className="text-xs text-muted-foreground">
              Private — only you and the review team ever see it (2 MB max).
              Please avoid faces and names.
            </p>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-xl w-full" data-testid="rate-save-button">
            {saving ? "Saving…" : "Save rating"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { RateDialog, Stars };

// Adult-only activity ratings. Aggregates hidden until 5+ ratings; listing is
// ordered by recency, never by stars. No identities are ever shown.
export const ActivityRatingsPanel = () => {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/ratings/activities").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  useEffect(load, [load]);

  if (!data || data.activities.length === 0) return null; // hidden when flag off / no activities

  const removeRating = async (submissionId) => {
    try {
      await api.delete(`/ratings/activities/${submissionId}`);
      toast.success("Your rating was deleted");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-3" data-testid="activity-ratings-panel">
      <div className="flex items-center gap-2">
        <Star className="h-4 w-4 text-amber-500" aria-hidden="true" />
        <p className="text-sm font-bold">Rate marketplace activities</p>
      </div>
      <p className="text-xs text-muted-foreground">
        Verified adults only. Ratings help other families discover activities but never override
        safety review, age suitability or your own settings.
      </p>
      <div className="space-y-2" data-testid="ratings-activity-list">
        {data.activities.slice(0, 6).map((a) => (
          <div key={a.submission_id} className="bg-muted/40 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid={`rating-row-${a.submission_id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{a.title}</p>
              <p className="text-xs text-muted-foreground" data-testid={`rating-aggregate-${a.submission_id}`}>
                {a.aggregate.visible ? a.aggregate.label : "New — not enough ratings yet"}
                {" · ages "}{a.age_bands.join(", ")}
              </p>
              {a.aggregate.category_summary && (
                <p className="text-xs text-secondary font-semibold" data-testid={`rating-insight-${a.submission_id}`}>
                  {a.aggregate.category_summary}
                </p>
              )}
              {a.my_rating && (
                <div className="flex items-center gap-2 mt-1">
                  <Stars value={a.my_rating.stars} testPrefix={`mine-${a.submission_id}`} />
                  <Badge variant="secondary" className="text-[10px]">Your rating</Badge>
                </div>
              )}
              {a.my_rating?.creator_note && (
                <p className="text-xs text-muted-foreground italic mt-1" data-testid={`creator-note-${a.submission_id}`}>
                  Note from the creator: “{a.my_rating.creator_note}”
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <RateDialog activity={a} onSaved={load} />
              {a.my_rating && (
                <Button variant="ghost" size="sm" className="rounded-full h-8 text-destructive"
                  onClick={() => removeRating(a.submission_id)}
                  aria-label="Delete my rating" data-testid={`rating-delete-${a.submission_id}`}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
